import { toast } from 'sonner';
import type { EducationFormData } from './EducationFormFields';
import type { Education, CreateEducationRequest, EducationLevel } from '../types/education.types';

export const DEFAULT_VALUES: EducationFormData = {
  educationLevel: '',
  institution: '',
  educationField: '',
  city: '',
  country: '',
  yearBegin: '',
  yearEnd: '',
};

export function toFormValues(edu: Education): EducationFormData {
  return {
    educationLevel: edu.educationLevel,
    institution: edu.institution,
    educationField: edu.educationField,
    city: edu.city,
    country: edu.country,
    yearBegin: edu.yearBegin.toString(),
    yearEnd: edu.yearEnd?.toString() ?? '',
  };
}

export function buildRequest(data: EducationFormData): CreateEducationRequest {
  return {
    educationLevel: data.educationLevel as EducationLevel,
    institution: data.institution,
    educationField: data.educationField,
    city: data.city,
    country: data.country,
    yearBegin: parseInt(data.yearBegin),
    yearEnd: data.yearEnd ? parseInt(data.yearEnd) : undefined,
  };
}

export function getLabels(
  mode: 'create' | 'edit',
  isLoading: boolean
): { title: string; submitLabel: string } {
  const isCreate = mode === 'create';
  return {
    title: isCreate ? 'Add Education' : 'Edit Education',
    submitLabel: isCreate
      ? isLoading
        ? 'Creating...'
        : 'Create'
      : isLoading
        ? 'Saving...'
        : 'Save',
  };
}

export function resetFormState(
  reset: (values: EducationFormData) => void,
  mode: 'create' | 'edit',
  education?: Education | null
): void {
  if (mode === 'edit' && education) reset(toFormValues(education));
  else if (mode === 'create') reset(DEFAULT_VALUES);
}

type MutationCallbacks = { onSuccess: () => void; onError: () => void };

interface SubmitParams {
  candidateId: string;
  request: CreateEducationRequest;
  onDone: () => void;
}

export function submitCreate(
  createFn: (
    params: { candidateId: string; request: CreateEducationRequest },
    callbacks: MutationCallbacks
  ) => void,
  { candidateId, request, onDone }: SubmitParams
): void {
  createFn(
    { candidateId, request },
    {
      onSuccess: () => {
        toast.success('Education created successfully');
        onDone();
      },
      onError: () => toast.error('Failed to create education'),
    }
  );
}

export function submitUpdate(
  updateFn: (
    params: { candidateId: string; educationId: string; request: CreateEducationRequest },
    callbacks: MutationCallbacks
  ) => void,
  educationId: string,
  { candidateId, request, onDone }: SubmitParams
): void {
  updateFn(
    { candidateId, educationId, request },
    {
      onSuccess: () => {
        toast.success('Education updated successfully');
        onDone();
      },
      onError: () => toast.error('Failed to update education'),
    }
  );
}
